/**
 * ixLatency — the SINGLE source of truth for the round-trip IX (Internet
 * Exchange) latency estimate derived from geographic proximity, not a surveyed fiber route.
 *
 * The deterministic infrastructure datasheet (`buildInfrastructureFromSed`,
 * `siteRagService.ts`) and the grounded-content canonical fact store
 * (`buildCanonicalFactStore`) BOTH need this figure. Before this module each
 * derived it inline as `nearestIxDistKm * 0.05`, so a future edit to one factor
 * could silently desync the datasheet metric ("10.245 ms") from the value the
 * narrative is told to use — exactly the class of cross-section inconsistency
 * this pass fixes. Deriving both from `estimateIxLatencyMs` guarantees they
 * agree.
 *
 * This is an ESTIMATE, not a measurement: a ~0.05 ms/km proxy for round-trip
 * fibre latency (light-in-fibre one-way ≈ 0.005 ms/km ×2 for round trip, plus a
 * routing/switching allowance). Callers surface it labelled as an estimate.
 *
 * PURE. No I/O. No DOM.
 */

/**
 * Round-trip latency proxy, milliseconds per km of geographic distance to the
 * nearest IX. Historical value lifted verbatim from the datasheet builder so
 * the derivation is byte-identical to today; centralised here so it can only be
 * changed in ONE place.
 */
export const IX_LATENCY_MS_PER_KM = 0.05;

/**
 * Estimate round-trip IX latency (ms) from geographic distance to the nearest
 * Internet Exchange (km). Returns `null` for a missing / non-finite / negative
 * distance so callers can render "no data" rather than a fabricated `0`/`NaN`.
 *
 * @param distKm distance to nearest IX in km (`ConnectivityData.nearestIxDistKm`)
 * @param round  when true (default) rounds to 1 decimal — the display precision
 *               the canonical fact + prose use; pass false for the raw product.
 */
export function estimateIxLatencyMs(
  distKm: number | null | undefined,
  round = true,
): number | null {
  if (typeof distKm !== 'number' || !Number.isFinite(distKm) || distKm < 0) {
    return null;
  }
  const ms = distKm * IX_LATENCY_MS_PER_KM;
  return round ? Math.round(ms * 10) / 10 : ms;
}
